import React, { useState, useEffect, useRef } from 'react';
import type { Order, Table, Reservation, FloorFixture } from '../../types';
import { getTableSeatsUsed, isFloorPlanActiveTableOrder } from '../../lib/orderUtils';
import { isReservationSoonForFloor, reservationHoldsTable } from '../../lib/reservationCapacity';
import { orderCreatedAtMs } from '../../lib/orderTimestamps';
import UsersIcon from '../icons/UsersIcon';
import TableDetailsModal from './TableDetailsModal';

interface FloorPlanViewProps {
  tables: Table[];
  orders: Order[];
  reservations?: Reservation[];
  layoutComponents?: FloorFixture[];
  onTableClick: (tableId: number) => void;
  isEditMode?: boolean;
  isMergeMode?: boolean;
  selectedTableIds?: number[];
  onTableMove?: (tableId: number, x: number, y: number) => void;
  onLayoutComponentMove?: (componentId: string, x: number, y: number) => void;
  onTableSelect?: (tableId: number) => void;
  onSplitTable?: (tableId: number) => void;
  onTransferOrder?: (orderId: string, tableId: number) => void;
  selectedComponentId?: string | null;
  onSelectComponent?: (id: string | null) => void;
  showGrid?: boolean;
  gridSize?: number;
  snapToGrid?: boolean;
  /** When user clicks "Seat new party" / start order from table details */
  onSeatNewParty?: (
    table: Table,
    context?: { partySize?: number; customerName?: string },
  ) => void;
  /** When user clicks "View / Edit" on an order in table details modal */
  onSelectOrder?: (order: Order) => void;
  /** When user clicks "End session early" for a buffet order in table details modal */
  onEndBuffetSession?: (order: Order) => void;
  /** Seat reservation guest (pending/confirmed → seated) from table details */
  onUpdateReservation?: (id: number, updates: Partial<Reservation>) => Promise<void> | void;
  /** Transfer reservation to another table before an order exists */
  onTransferReservation?: (reservation: Reservation, sourceTable: Table) => void;
  /** Optional scale factor for table sizes (1 = normal, <1 = smaller) */
  tableScale?: number;
  /** Scale the entire floor to fit the container (no cropping) */
  fitToContainer?: boolean;
  /** Show only reservation status per table (no ordering/seat actions) */
  reservationOnly?: boolean;
  /** When set, table details modal shows reservations for this date only (e.g. selected date on Reservations page) */
  reservationDate?: string;
  /** Business timezone for “today’s” open tabs (defaults to Pacific/Auckland). */
  businessTimeZone?: string;
}

// Fixed container dimensions for consistent positioning
const FLOOR_PLAN_WIDTH = 1200;
const FLOOR_PLAN_HEIGHT = 800;

const TableComponent: React.FC<{
    table: Table;
    order?: Order;
    reservation?: Reservation;
    customerCount?: number;
    seatsUsed?: number;
    spareSeats?: number;
    onClick: (id: number) => void;
    isEditMode?: boolean;
    isMergeMode?: boolean;
    isSelected?: boolean;
    onTableMove?: (tableId: number, x: number, y: number) => void;
    onTableSelect?: (tableId: number) => void;
    onSplitTable?: (tableId: number) => void;
    onTransferOrder?: (orderId: string, tableId: number) => void;
    containerRef: React.RefObject<HTMLDivElement>;
    containerScale: { x: number; y: number };
    orders?: Order[];
    onShowDetails?: (table: Table) => void;
    sizeScale?: number;
    businessTimeZone?: string;
}> = ({
    table,
    order,
    reservation,
    customerCount,
    seatsUsed = 0,
    spareSeats = 0,
    onClick,
    isEditMode,
    isMergeMode,
    isSelected,
    onTableMove,
    onTableSelect,
    onSplitTable,
    onTransferOrder,
    containerRef,
    containerScale,
    orders = [],
    onShowDetails,
    sizeScale = 1,
    businessTimeZone = 'Pacific/Auckland',
  }) => {
    const tableIdNum = Number(table.id);
    const tableOrders = orders.filter((o) => isFloorPlanActiveTableOrder(o, tableIdNum, businessTimeZone));
    const primaryOrder = order || (tableOrders.find(o => o.status === 'served') ?? tableOrders[0]);
    const hasActiveOrder = tableOrders.length > 0;
    const [elapsedTime, setElapsedTime] = useState('');
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
    const longPressDelay = 500;

    useEffect(() => {
        if (!hasActiveOrder || !primaryOrder) {
            setElapsedTime('');
            return;
        }
        const updateTime = () => {
            const placedMs = orderCreatedAtMs(primaryOrder);
            const diff = Date.now() - (placedMs > 0 ? placedMs : Date.now());
            const minutes = Math.floor(diff / 60000);
            const seconds = Math.floor((diff % 60000) / 1000);
            setElapsedTime(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
        }
        updateTime();
        const interval = setInterval(updateTime, 1000);
        return () => clearInterval(interval);
    }, [hasActiveOrder, primaryOrder]);

    const handleTouchStart = (e: React.TouchEvent) => {
      if (isEditMode || isMergeMode) return;
      const timer = setTimeout(() => {
        if (onShowDetails) {
          onShowDetails(table);
        }
      }, longPressDelay);
      setLongPressTimer(timer);
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
      }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
      }
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (isEditMode && onTableMove && containerRef.current) {
            e.preventDefault();
            e.stopPropagation();
            
            const tableElement = e.currentTarget as HTMLElement;
            const container = containerRef.current;
            const containerRect = container.getBoundingClientRect();
            
            // Convert percentage to fixed coordinates
            const tableX = ((table.x || 50) / 100) * FLOOR_PLAN_WIDTH;
            const tableY = ((table.y || 50) / 100) * FLOOR_PLAN_HEIGHT;
            
            // Get mouse position in fixed coordinate space
            const getMousePos = (clientX: number, clientY: number) => {
                const x = (clientX - containerRect.left) / containerScale.x;
                const y = (clientY - containerRect.top) / containerScale.y;
                return { x, y };
            };
            
            const startPos = getMousePos(e.clientX, e.clientY);
            const startTableX = tableX;
            const startTableY = tableY;

            const handleMouseMove = (moveEvent: MouseEvent) => {
                const currentPos = getMousePos(moveEvent.clientX, moveEvent.clientY);
                const dx = currentPos.x - startPos.x;
                const dy = currentPos.y - startPos.y;
                tableElement.style.transform = `translate(-50%, -50%) translate(${dx * containerScale.x}px, ${dy * containerScale.y}px)`;
            };

            const handleMouseUp = (upEvent: MouseEvent) => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                
                tableElement.style.transform = `translate(-50%, -50%)`;

                const endPos = getMousePos(upEvent.clientX, upEvent.clientY);
                const dx = endPos.x - startPos.x;
                const dy = endPos.y - startPos.y;
                
                // Calculate new position in fixed coordinates
                const newX = Math.max(0, Math.min(FLOOR_PLAN_WIDTH, startTableX + dx));
                const newY = Math.max(0, Math.min(FLOOR_PLAN_HEIGHT, startTableY + dy));
                
                // Convert back to percentage
                const newXPercent = (newX / FLOOR_PLAN_WIDTH) * 100;
                const newYPercent = (newY / FLOOR_PLAN_HEIGHT) * 100;

                // Round to 2 decimal places
                const clampedX = Math.round(newXPercent * 100) / 100;
                const clampedY = Math.round(newYPercent * 100) / 100;
                
                onTableMove(table.id, clampedX, clampedY);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        } else if (!isMergeMode && !isEditMode) {
            const timer = setTimeout(() => {
                if (onShowDetails) {
                    onShowDetails(table);
                }
            }, longPressDelay);
            setLongPressTimer(timer);
            const handleMouseUp = () => {
                if (timer) {
                    clearTimeout(timer);
                    setLongPressTimer(null);
                }
                document.removeEventListener('mouseup', handleMouseUp);
            };
            document.addEventListener('mouseup', handleMouseUp);
        }
    };

    const isServed = primaryOrder?.status === 'served';
    // Prefer live order/reservation truth over stale Firestore table.status (avoids red↔green blink
    // after merge payment while status sync catches up).
    const displayStatus = isServed
      ? 'served'
      : hasActiveOrder || reservation?.status === 'seated'
        ? 'occupied'
        : reservation &&
            (reservation.status === 'pending' || reservation.status === 'confirmed') &&
            isReservationSoonForFloor(reservation, new Date())
          ? 'reserved'
          : 'available';
    const isMerged = table.mergedWith && table.mergedWith.length > 1;
    const primaryId = table.mergedWith?.[0];
    const isPrimaryMerged = isMerged && primaryId != null && Number(primaryId) === tableIdNum;

    const statusClasses = {
        available: 'bg-green-500 hover:bg-green-600',
        occupied: 'bg-red-500 hover:bg-red-600',
        reserved: 'bg-yellow-500 hover:bg-yellow-600',
        served: 'bg-blue-500 hover:bg-blue-600',
    };

    const mergedClasses = isMerged && isPrimaryMerged 
      ? 'ring-2 ring-purple-400 ring-offset-2' 
      : isMerged && !isPrimaryMerged
      ? 'opacity-50'
      : '';
    
    const selectedClasses = isSelected && isMergeMode
      ? 'ring-4 ring-blue-400 ring-offset-2'
      : '';

    const baseShapeClasses = {
        square: 'rounded-lg w-28 h-22 min-w-0',
        round: 'rounded-full w-24 h-24 min-w-0',
    };

    const scaleClass =
      sizeScale < 0.9 ? 'scale-75' :
      sizeScale < 1   ? 'scale-90' :
      sizeScale > 1.1 ? 'scale-110' :
      sizeScale > 1   ? 'scale-105' :
      '';

    const shapeClasses = {
        square: `${baseShapeClasses.square}`,
        round: `${baseShapeClasses.round}`,
    };

    const formatReservationInfo = (res: Reservation) => {
      const date = new Date(res.reservationDate);
      const isToday = date.toDateString() === new Date().toDateString();
      const dateStr = isToday ? 'Today' : date.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' });
      return `${dateStr} ${res.reservationTime}`;
    };

    const handleClick = (e: React.MouseEvent) => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
      }
      if (isMergeMode && onTableSelect) {
        e.stopPropagation();
        onTableSelect(table.id);
      } else if (!isEditMode && onShowDetails) {
        e.stopPropagation();
        onShowDetails(table);
      } else {
        onClick(table.id);
      }
    };

    const handleContextMenu = (e: React.MouseEvent) => {
      e.preventDefault();
      if (isMerged && isPrimaryMerged && onSplitTable) {
        onSplitTable(table.id);
      }
    };

    const tableX = typeof table.x === 'number' && !isNaN(table.x) ? table.x : 50;
    const tableY = typeof table.y === 'number' && !isNaN(table.y) ? table.y : 50;

    return (
        <div
            className={`absolute transform -translate-x-1/2 -translate-y-1/2 ${mergedClasses} ${selectedClasses}`}
            style={{ 
              left: `${tableX}%`, 
              top: `${tableY}%`,
              position: 'absolute',
            }}
        >
        <button
            onMouseDown={handleMouseDown}
            onClick={handleClick}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchMove}
            onContextMenu={handleContextMenu}
            data-testid={`floor-table-${table.id}`}
            data-table-name={table.name}
            data-table-occupied={displayStatus === 'occupied' ? 'true' : 'false'}
            className={`flex flex-col items-center justify-center text-white font-bold shadow-lg transition-transform hover:scale-105 overflow-hidden px-2 pt-2 pb-1.5 box-border gap-y-1 ${statusClasses[displayStatus]} ${shapeClasses[table.shape || 'square']} ${scaleClass} ${isEditMode ? 'cursor-grab active:cursor-grabbing' : isMergeMode ? 'cursor-pointer' : 'cursor-pointer'}`}
            title={reservation ? `Reserved: ${formatReservationInfo(reservation)}` : isMerged ? 'Merged Table' : `Click for details • ${table.name}`}
        >
        <span className="w-full min-w-0 truncate text-center text-xs font-bold leading-snug pt-0.5" title={table.name}>{table.name}</span>
        {isMerged && isPrimaryMerged && (
          <span className="text-[8px] font-normal bg-purple-600 px-1 rounded shrink-0">MERGED</span>
        )}
        <span className="text-[10px] font-normal flex items-center justify-center shrink-0 leading-none"><UsersIcon className="w-3 h-3 mr-0.5 shrink-0" />{seatsUsed > 0 ? `${seatsUsed}/${table.capacity}` : table.capacity}</span>
        </button>
        </div>
    );
};

const LayoutComponent: React.FC<{
  component: FloorFixture;
  isEditMode?: boolean;
  isSelected?: boolean;
  onMove?: (id: string, x: number, y: number) => void;
  onClick?: (id: string) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  containerScale: { x: number; y: number };
  snapToGrid?: boolean;
  gridSize?: number;
}> = ({ component, isEditMode, isSelected, onMove, onClick, containerRef, containerScale, snapToGrid, gridSize = 5 }) => {
  const snapToGridValue = (value: number): number => {
    if (!snapToGrid) return value;
    return Math.round(value / gridSize) * gridSize;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isEditMode || !onMove || !containerRef.current) {
      if (onClick) {
        onClick(component.id);
      }
      return;
    }
    e.preventDefault();
    e.stopPropagation();

    const element = e.currentTarget as HTMLElement;
    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    
    // Convert percentage to fixed coordinates
    const compX = (component.x / 100) * FLOOR_PLAN_WIDTH;
    const compY = (component.y / 100) * FLOOR_PLAN_HEIGHT;
    
    // Get mouse position in fixed coordinate space
    const getMousePos = (clientX: number, clientY: number) => {
      const x = (clientX - containerRect.left) / containerScale.x;
      const y = (clientY - containerRect.top) / containerScale.y;
      return { x, y };
    };
    
    const startPos = getMousePos(e.clientX, e.clientY);
    const startCompX = compX;
    const startCompY = compY;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const currentPos = getMousePos(moveEvent.clientX, moveEvent.clientY);
      const dx = currentPos.x - startPos.x;
      const dy = currentPos.y - startPos.y;
      element.style.transform = `translate(-50%, -50%) translate(${dx * containerScale.x}px, ${dy * containerScale.y}px)`;
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      element.style.transform = 'translate(-50%, -50%)';

      const endPos = getMousePos(upEvent.clientX, upEvent.clientY);
      const dx = endPos.x - startPos.x;
      const dy = endPos.y - startPos.y;
      
      // Calculate new position in fixed coordinates
      const newX = Math.max(0, Math.min(FLOOR_PLAN_WIDTH, startCompX + dx));
      const newY = Math.max(0, Math.min(FLOOR_PLAN_HEIGHT, startCompY + dy));
      
      // Convert back to percentage
      const newXPercent = (newX / FLOOR_PLAN_WIDTH) * 100;
      const newYPercent = (newY / FLOOR_PLAN_HEIGHT) * 100;

      // Clamp and snap to grid
      const clampedX = Math.round(newXPercent * 100) / 100;
      const clampedY = Math.round(newYPercent * 100) / 100;
      const snappedX = snapToGridValue(clampedX);
      const snappedY = snapToGridValue(clampedY);

      onMove(component.id, snappedX, snappedY);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const baseClasses = {
    entrance: 'bg-gray-200 border border-gray-300 text-gray-600 font-semibold tracking-widest text-xs uppercase flex items-center justify-center',
    bar: 'bg-yellow-800 text-white font-bold tracking-widest flex items-center justify-center rounded-md shadow-md',
    patio: 'bg-green-100 border-2 border-dashed border-green-400 text-green-700 font-semibold flex items-center justify-center rounded-md',
    kitchen: 'bg-orange-100 border-2 border-orange-400 text-orange-700 font-semibold flex items-center justify-center rounded-md',
    restroom: 'bg-blue-100 border-2 border-blue-400 text-blue-700 font-semibold flex items-center justify-center rounded-md',
    storage: 'bg-gray-300 border-2 border-gray-500 text-gray-700 font-semibold flex items-center justify-center rounded-md',
    counter: 'bg-amber-100 border-2 border-amber-400 text-amber-700 font-semibold flex items-center justify-center rounded-md',
    window: 'bg-sky-100 border-2 border-sky-400 text-sky-700 font-semibold flex items-center justify-center rounded-md',
    wall: 'bg-gray-400 border border-gray-600 text-gray-800 font-semibold flex items-center justify-center',
    custom: 'bg-purple-100 border border-purple-300 text-purple-700 font-semibold flex items-center justify-center rounded-md',
  };

  const className = baseClasses[component.type] || baseClasses.custom;
  const selectedClass = isSelected ? 'ring-4 ring-pink-500 ring-offset-2' : '';

  const width = component.width ?? 10;
  const height = component.height ?? 10;
  const rotation = component.rotation || 0;
  const x = typeof component.x === 'number' && !isNaN(component.x) ? component.x : 50;
  const y = typeof component.y === 'number' && !isNaN(component.y) ? component.y : 50;

  return (
    <div
      onMouseDown={handleMouseDown}
      onClick={() => onClick && onClick(component.id)}
      className={`absolute ${className} ${selectedClass} ${isEditMode ? 'cursor-move' : 'cursor-pointer'} select-none text-center px-2 transition-all`}
      style={{
        left: `${x}%`,
        top: `${y}%`,
        width: `${width}%`,
        height: `${height}%`,
        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
        position: 'absolute',
      }}
    >
      <span className="px-2 text-sm">{component.label.toUpperCase()}</span>
    </div>
  );
};

const FloorPlanView: React.FC<FloorPlanViewProps> = ({ tables, orders, reservations = [], layoutComponents = [], onTableClick, isEditMode, isMergeMode, selectedTableIds = [], onTableMove, onLayoutComponentMove, onTableSelect, onSplitTable, onTransferOrder, selectedComponentId, onSelectComponent, showGrid = false, gridSize = 5, snapToGrid = false, onSeatNewParty, onSelectOrder, onEndBuffetSession, onUpdateReservation, onTransferReservation, tableScale = 1, fitToContainer = false, reservationOnly = false, reservationDate, businessTimeZone = 'Pacific/Auckland' }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const fitWrapperRef = useRef<HTMLDivElement>(null);
    const [selectedTableForDetails, setSelectedTableForDetails] = useState<Table | null>(null);
    const [containerScale, setContainerScale] = useState({ x: 1, y: 1 });
    const [fitScale, setFitScale] = useState(1);
    
    const now = new Date();
    const todayIso = now.toLocaleDateString('en-CA', { timeZone: businessTimeZone });

    const activeReservations =
      reservationOnly && reservationDate
        ? reservations.filter(
            (res) =>
              (res.status === 'pending' || res.status === 'confirmed' || res.status === 'seated') &&
              res.reservationDate === reservationDate
          )
        : reservations.filter(
            (res) =>
              (res.status === 'seated' && res.reservationDate === todayIso) ||
              isReservationSoonForFloor(res, now),
          );

    // When fitToContainer: scale entire floor to fit wrapper
    useEffect(() => {
        if (!fitToContainer || !fitWrapperRef.current) return;
        const updateFitScale = () => {
            const wrapper = fitWrapperRef.current;
            if (wrapper) {
                const w = wrapper.offsetWidth;
                const h = wrapper.offsetHeight;
                const scale = Math.min(w / FLOOR_PLAN_WIDTH, h / FLOOR_PLAN_HEIGHT, 1);
                setFitScale(scale);
                setContainerScale({ x: scale, y: scale });
            }
        };
        updateFitScale();
        const ro = new ResizeObserver(updateFitScale);
        ro.observe(fitWrapperRef.current);
        return () => ro.disconnect();
    }, [fitToContainer]);

    // Calculate scale based on actual container size (when not fitToContainer)
    useEffect(() => {
        if (fitToContainer || !containerRef.current) return;
        const updateScale = () => {
            const container = containerRef.current;
            if (container) {
                const scaleX = container.offsetWidth / FLOOR_PLAN_WIDTH;
                const scaleY = container.offsetHeight / FLOOR_PLAN_HEIGHT;
                setContainerScale({ x: scaleX, y: scaleY });
            }
        };
        updateScale();
        window.addEventListener('resize', updateScale);
        return () => window.removeEventListener('resize', updateScale);
    }, [fitToContainer]);

    const renderGrid = () => {
      if (!showGrid || !containerRef.current) return null;
      const gridLines = [];
      for (let i = 0; i <= 100; i += gridSize) {
        gridLines.push(
          <div
            key={`v-${i}`}
            className="absolute border-l border-gray-300 opacity-30"
            style={{
              left: `${i}%`,
              top: 0,
              height: '100%',
              width: '1px',
            }}
          />
        );
        gridLines.push(
          <div
            key={`h-${i}`}
            className="absolute border-t border-gray-300 opacity-30"
            style={{
              top: `${i}%`,
              left: 0,
              width: '100%',
              height: '1px',
            }}
          />
        );
      }
      return <div className="absolute inset-0 pointer-events-none">{gridLines}</div>;
    };

    const floorContent = (
        <>
            {renderGrid()}
            <div className="absolute top-0 left-0 h-full w-4 bg-gray-200" title="Wall"></div>
            <div className="absolute top-0 right-0 h-full w-4 bg-gray-200" title="Wall"></div>
            <div className="absolute bottom-0 left-0 w-full h-4 bg-gray-200" title="Wall"></div>
            {layoutComponents.map(component => (
              <LayoutComponent
                key={component.id}
                component={component}
                isEditMode={isEditMode}
                isSelected={selectedComponentId === component.id}
                onMove={onLayoutComponentMove}
                onClick={onSelectComponent}
                containerRef={containerRef}
                containerScale={containerScale}
                snapToGrid={snapToGrid}
                gridSize={gridSize}
              />
            ))}
            {tables.map((table, tableIndex) => {
              const tableIdNum = Number(table.id);
              const tableOrders = orders.filter((o) => isFloorPlanActiveTableOrder(o, tableIdNum, businessTimeZone));
              const order = tableOrders.find(o => o.status === 'served') ?? tableOrders[0];
              const customerCount = tableOrders.length;
              const seatsUsed = getTableSeatsUsed(tableOrders);
              const spareSeats = Math.max(0, table.capacity - seatsUsed);
              const tableReservations = activeReservations
                .filter(r => reservationHoldsTable(r, table.id))
                .sort((a, b) => {
                  // Prefer seated for paint/details, then soonest time
                  if (a.status === 'seated' && b.status !== 'seated') return -1;
                  if (b.status === 'seated' && a.status !== 'seated') return 1;
                  const dateCompare = a.reservationDate.localeCompare(b.reservationDate);
                  if (dateCompare !== 0) return dateCompare;
                  return a.reservationTime.localeCompare(b.reservationTime);
                });
              const reservation = tableReservations[0];
              const isSelected = selectedTableIds.includes(table.id);
              const isMerged = table.mergedWith && table.mergedWith.length > 1;
              const primaryId = table.mergedWith?.[0];
              const isPrimaryMerged = isMerged && primaryId != null && Number(primaryId) === tableIdNum;
              const shouldShow = !isMerged || isPrimaryMerged;
              if (!shouldShow) return null;
              return (
                <TableComponent
                  key={`table-${(table as { firestoreId?: string }).firestoreId ?? table.id}-${tableIndex}`}
                  table={table}
                  order={order}
                  reservation={reservation}
                  customerCount={customerCount}
                  seatsUsed={seatsUsed}
                  spareSeats={spareSeats}
                  onClick={onTableClick}
                  isEditMode={isEditMode}
                  isMergeMode={isMergeMode}
                  isSelected={isSelected}
                  onTableMove={onTableMove}
                  onTableSelect={onTableSelect}
                  onSplitTable={onSplitTable}
                  onTransferOrder={onTransferOrder}
                  containerRef={containerRef}
                  containerScale={containerScale}
                  sizeScale={tableScale}
                  orders={orders}
                  onShowDetails={(t) => setSelectedTableForDetails(t)}
                  businessTimeZone={businessTimeZone}
                />
              );
            })}
        </>
    );

    const innerFloorStyle = fitToContainer
      ? { width: FLOOR_PLAN_WIDTH, height: FLOOR_PLAN_HEIGHT, transform: `scale(${fitScale})`, transformOrigin: '0 0' as const }
      : {
          width: '100%',
          maxWidth: `${FLOOR_PLAN_WIDTH}px`,
          height: '100%',
          minHeight: '600px',
          aspectRatio: `${FLOOR_PLAN_WIDTH} / ${FLOOR_PLAN_HEIGHT}`,
          position: 'relative' as const,
        };

    return (
        <>
        {fitToContainer ? (
          <div ref={fitWrapperRef} className="w-full h-full min-h-0 overflow-hidden bg-gray-100 rounded-lg">
            <div ref={containerRef} className="relative bg-white rounded-lg shadow-inner select-none" style={innerFloorStyle}>
              {floorContent}
            </div>
          </div>
        ) : (
        <div className="flex-1 overflow-auto p-6 bg-gray-100">
          <div ref={containerRef} className="relative bg-white rounded-lg shadow-inner select-none mx-auto" style={innerFloorStyle}>
            {floorContent}
          </div>
        </div>
        )}
        {selectedTableForDetails && (
          <TableDetailsModal
            table={selectedTableForDetails}
            orders={orders}
            tables={tables}
            reservations={reservations}
            onClose={() => setSelectedTableForDetails(null)}
            reservationOnly={reservationOnly}
            reservationDate={reservationDate}
            onSeatNewParty={!reservationOnly && onSeatNewParty ? (t, ctx) => { setSelectedTableForDetails(null); onSeatNewParty(t, ctx); } : undefined}
            onSelectOrder={!reservationOnly && onSelectOrder ? (o) => { setSelectedTableForDetails(null); onSelectOrder(o); } : undefined}
            onEndBuffetSession={!reservationOnly && onEndBuffetSession ? (o) => { setSelectedTableForDetails(null); onEndBuffetSession(o); } : undefined}
            onUpdateReservation={!reservationOnly ? onUpdateReservation : undefined}
            onTransferReservation={
              !reservationOnly && onTransferReservation
                ? (r, t) => {
                    setSelectedTableForDetails(null);
                    onTransferReservation(r, t);
                  }
                : undefined
            }
          />
        )}
        </>
    );
};

export default FloorPlanView;
